import React, { createContext, useContext, useEffect, useReducer } from 'react';

// Storage key constant
const STORAGE_KEY = 'vettingAppSession';

// Session duration in milliseconds (8 hours)
const SESSION_DURATION = 8 * 60 * 60 * 1000;

// Helper functions for localStorage operations
const storageHelpers = {
  save: (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  },
  
  load: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  },
  
  remove: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }
};

// Utility to check if a session is expired
const isSessionExpired = (expiresAt) => {
  return !expiresAt || new Date().getTime() > expiresAt;
};

// Initial state for the reducer
const initialState = {
  sessionData: null,
  isSessionValid: false,
  eid: '',
  isLoading: true
};

// Reducer function for state management
const sessionReducer = (state, action) => {
  switch (action.type) {
    case 'INITIALIZE_SESSION':
      return {
        ...state,
        sessionData: action.payload.userData,
        eid: action.payload.eid,
        isSessionValid: true,
        isLoading: false
      };
    case 'UPDATE_SESSION':
      return {
        ...state,
        sessionData: { ...state.sessionData, ...action.payload }
      };
    case 'CLEAR_SESSION':
      return {
        ...initialState,
        isLoading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    default:
      return state;
  }
};

// Create the context
const SessionContext = createContext();

// Custom hook to use the session context
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

// Provider component
export const SessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const { sessionData, isSessionValid, eid, isLoading } = state;

  // Load session data from localStorage on mount
  useEffect(() => {
    const storedSession = storageHelpers.load();
    
    if (storedSession) {
      // Check if the session is still valid (not expired)
      if (!isSessionExpired(storedSession.expiresAt)) {
        dispatch({
          type: 'INITIALIZE_SESSION',
          payload: {
            userData: storedSession.data,
            eid: storedSession.eid
          }
        });
      } else {
        // Session expired, clear it
        storageHelpers.remove();
      }
    }
    
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  // Initialize session with user data
  const initializeSession = (userData, eid) => {
    if (!userData || !eid) {
      return false;
    }

    // Create session with expiration
    const expiresAt = new Date().getTime() + SESSION_DURATION;
    const sessionInfo = {
      data: userData,
      eid: eid,
      expiresAt,
      startedAt: new Date().getTime()
    };

    // Save to state and localStorage
    dispatch({
      type: 'INITIALIZE_SESSION',
      payload: {
        userData,
        eid: eid
      }
    });
    
    storageHelpers.save(sessionInfo);
    return true;
  };

  // Clear session data
  const clearSession = () => {
    dispatch({ type: 'CLEAR_SESSION' });
    storageHelpers.remove();
  };

  // Validate if the session exists and has required data
  const validateSession = () => {
    // Don't validate while still loading
    if (isLoading) {
      return false;
    }
    
    // Check if session is valid and has required fields (email is not mandatory)
    const isValid = (
      isSessionValid &&
      sessionData &&
      eid &&
      sessionData.fname
    );
    
    // Log validation failures for debugging
    if (!isValid && sessionData) {
      if (!sessionData.fname) {
        console.error('Session validation failed: Missing first name');
      }
      if (!eid) {
        console.error('Session validation failed: Missing evaluation ID');
      }
    }
    
    return isValid;
  };

  // Update specific fields in the session data
  const updateSessionData = (updates) => {
    if (!sessionData || !isSessionValid) {
      return false;
    }

    // Get the current session info from localStorage
    const storedSession = storageHelpers.load();
    if (!storedSession) {
      return false;
    }
    
    // Update the session data
    const updatedData = { ...sessionData, ...updates };
    storedSession.data = updatedData;
    
    // Save updated session info back to localStorage
    if (!storageHelpers.save(storedSession)) {
      return false;
    }
    
    // Update state
    dispatch({ type: 'UPDATE_SESSION', payload: updates });
    return true;
  };

  const value = {
    sessionData,
    eid,
    isSessionValid,
    isLoading,
    initializeSession,
    clearSession,
    validateSession,
    updateSessionData
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContext;
